import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Primitives";
import Pagination, { usePagination } from "../components/Pagination";
import { Plus, ShieldCheck, Trash2, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { useApiLoading, LoadingRow } from "../components/TableLoader";

export default function Domains() {
    const { user } = useAuth();
    const [agencies, setAgencies] = useState([]);
    const [agencyId, setAgencyId] = useState(user.role === "admin" ? user.agency_id : "");
    const [domains, setDomains] = useState([]);
    const apiLoading = useApiLoading();
    const [form, setForm] = useState({ domain: "", is_subdomain: false });

    useEffect(() => {
        if (user.role === "super_admin") {
            api.get("/agencies").then((r) => {
                setAgencies(r.data);
                if (!agencyId && r.data.length) setAgencyId(r.data[0].id);
            });
        }
    }, [user.role]); // eslint-disable-line

    const load = () => {
        if (agencyId) api.get(`/agencies/${agencyId}/domains`).then((r) => setDomains(r.data));
    };
    useEffect(load, [agencyId]); // eslint-disable-line

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/agencies/${agencyId}/domains`, form);
            toast.success("Domain added");
            setForm({ domain: "", is_subdomain: false });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const verify = async (id) => {
        try {
            await api.post(`/agencies/${agencyId}/domains/${id}/verify`);
            toast.success("Verified");
            load();
        } catch {
            toast.error("Failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this domain mapping?")) return;
        await api.delete(`/agencies/${agencyId}/domains/${id}`);
        load();
    };

    const pager = usePagination(domains, 25);

    return (
        <div data-testid="domains-page">
            <PageHeader eyebrow="White-label" title="Custom domains" subtitle="Map full domains or subdomains to this agency. Tenant resolution is keyed on the request host." />

            {user.role === "super_admin" && (
                <div className="mb-4">
                    <label className="overline">Agency</label>
                    <select value={agencyId} onChange={(e) => setAgencyId(e.target.value)} className="ml-2 px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="domain-agency-select">
                        {agencies.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                    </select>
                </div>
            )}

            <form onSubmit={submit} className="bg-white border border-zinc-200 p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="domain-create-form">
                <div className="flex-1 min-w-[260px]">
                    <label className="overline">Domain</label>
                    <input required value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value.toLowerCase() })} placeholder="reports.clientagency.com" className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded font-mono text-sm" data-testid="domain-input" />
                </div>
                <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
                    <input type="checkbox" checked={form.is_subdomain} onChange={(e) => setForm({ ...form, is_subdomain: e.target.checked })} />
                    Subdomain of this platform
                </label>
                <button type="submit" className="px-4 py-2 rounded text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="domain-submit-btn">
                    <Plus className="inline h-4 w-4 mr-1" />Add
                </button>
            </form>

            <div className="bg-white border border-zinc-200">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-zinc-100 border-b border-zinc-200">
                            <th className="overline text-left p-3">Domain</th>
                            <th className="overline text-left p-3">Type</th>
                            <th className="overline text-left p-3">Verification</th>
                            <th className="overline text-left p-3">SSL</th>
                            <th className="overline text-left p-3">Active</th>
                            <th className="overline text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pager.slice.map((d) => (
                            <tr key={d.id} className="border-b border-zinc-200 last:border-0">
                                <td className="p-3"><span className="inline-flex items-center gap-2 text-zinc-900 font-mono text-xs"><Globe2 className="h-3.5 w-3.5 text-zinc-500" />{d.domain}</span></td>
                                <td className="p-3 text-zinc-500 text-xs">{d.is_subdomain ? "Subdomain" : "Full domain"}</td>
                                <td className="p-3"><span className={`px-2 py-0.5 rounded text-[10px] uppercase ${d.verification_status === "verified" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : d.verification_status === "pending" ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>{d.verification_status}</span></td>
                                <td className="p-3"><span className={`px-2 py-0.5 rounded text-[10px] uppercase ${d.ssl_status === "active" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-zinc-200 text-zinc-500"}`}>{d.ssl_status}</span></td>
                                <td className="p-3 text-zinc-700">{d.is_active ? "Yes" : "—"}</td>
                                <td className="p-3 text-right">
                                    <div className="inline-flex items-center gap-3 text-xs">
                                        {d.verification_status !== "verified" && (
                                            <button onClick={() => verify(d.id)} className="inline-flex items-center gap-1 text-zinc-700 hover:wl-accent-text" data-testid={`verify-${d.id}`}>
                                                <ShieldCheck className="h-3.5 w-3.5" />verify
                                            </button>
                                        )}
                                        <button onClick={() => remove(d.id)} className="inline-flex items-center gap-1 text-red-400 hover:text-red-300" data-testid={`delete-domain-${d.id}`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!domains.length && (apiLoading ? <LoadingRow colSpan={6} /> : <tr><td colSpan={6} className="p-10 text-center text-zinc-500">No domains configured for this agency.</td></tr>)}
                    </tbody>
                </table>
                {domains.length > 0 && (
                    <Pagination
                        page={pager.page}
                        totalPages={pager.totalPages}
                        pageSize={pager.pageSize}
                        range={pager.range}
                        setPage={pager.setPage}
                        setPageSize={pager.setPageSize}
                        dataTestId="domains-pagination"
                    />
                )}
            </div>
        </div>
    );
}
